import React, { Component } from 'react'
import { Link } from 'react-router'
import { connect } from 'react-redux'
import { StickyContainer, Sticky } from 'react-sticky'

import classNames from './Timeline.css'
import { searchImages } from '../redux/search'
import { fetchHierarchy, fetchAlbumPathCounts } from '../redux/hierarchy'
import { createImageGetter } from '../utils/images'
import { tidyAlbumTitle } from '../utils/string'
import TimelinePeriod from '../components/TimelinePeriod'
import LoadingSpinner from '../components/LoadingSpinner'
import { TIMELINE_SEASONS, UNKNOWN_SEASON, YEAR_SELECTS } from '../constants'

const STARTING_DECADE = 1880
const CURRENT_YEAR = new Date().getFullYear()
const CURRENT_DECADE = Math.floor(CURRENT_YEAR / 10) * 10

class Timeline extends Component {
  state = {
    showNext: 0,
    showPrevious: 0,
    expanded: []
  }
  componentDidMount () {
    const { hierarchy, fetchHierarchy } = this.props
    if (!hierarchy.data) {
      fetchHierarchy()
    }
    const { pathCounts, fetchAlbumPathCounts } = this.props
    if (!pathCounts.data) {
      fetchAlbumPathCounts()
    }
  }
  componentWillReceiveProps (nextProps) {
    const { season, year } = this.props.params
    if (season !== nextProps.params.season || year !== nextProps.params.year) {
      this.setState({
        showNext: 0,
        showPrevious: 0
      })
    }
  }
  isExpanded (path) {
    return this.state.expanded.indexOf(path) !== -1
  }
  toggleExpanded = path => {
    if (this.isExpanded(path)) {
      this.setState({ expanded: this.state.expanded.filter(p => p !== path) })
    } else {
      this.setState({ expanded: this.state.expanded.concat(path) })
    }
  }
  renderBreadcrumbs (decade, year, season) {
    if (!decade) {
      return null
    }
    const crumbs = [
      { text: 'Timeline', href: '/timeline' },
      { text: `${decade}s`, href: `/timeline/${decade}` }
    ]
    if (year) {
      crumbs.push({ text: year, href: `/timeline/${decade}/${year}` })
    }
    if (season) {
      crumbs.push({ text: season.slice(3), href: `/timeline/${decade}/${year}/${season}` })
    }
    return (
      <Sticky key={'breadcrumbs' + decade + year + season} className={classNames.breadcrumbs}>
        {crumbs.map(link =>
          <Link key={link.href} to={link.href} className={classNames.breadcrumb}>
            {link.text}
          </Link>
        )}
      </Sticky>
    )
  }
  renderPeriod = (period, previous) => {
    const { searchImages, search, getImageSource } = this.props
    const { title, href, count, hideIfZero } = period
    if (count === 0) {
      if (hideIfZero) {
        return null
      }
      // Really hacky.. Return null if last 4 array elements are all gaps
      const prevGaps = previous instanceof Array && previous.slice(-4).filter(o => !o || /^gap-/.test(o.key)).length
      if (prevGaps === 4) {
        return null
      }
      return <li key={`gap-${href}`} className={classNames.gap} />
    }
    const expanded = period.path && this.isExpanded(period.path[0])
    const onClickExpand = e => {
      e.stopPropagation()
      this.toggleExpanded(period.path[0])
    }
    let periods = [
      <TimelinePeriod
        {...period}
        key={href}
        title={tidyAlbumTitle(title)}
        search={search}
        searchImages={searchImages}
        getImageSource={getImageSource}
        expanded={expanded}
        onClickExpand={period.subevents ? onClickExpand : null}
      />
    ]
    if (expanded) {
      periods = periods.concat(period.subevents.map(this.renderPeriod))
    }
    return periods
  }
  getOthers (decade, year) {
    const { hierarchy } = this.props

    if (!hierarchy.data) {
      return []
    }
    return hierarchy.data['<leaves>'].reduce((periods, library) => {
      if (year) {
        const data = hierarchy.data[library][`${decade}s`]
        if (data && data['<leaves>'].indexOf(year) !== -1 && !data[year]) {
          const path = `/COLLECTIONS/${library}/${decade}s/${year}`
          periods.push(this.renderPeriod({
            title: `Other pictures in ${year} (${library})`,
            path: [path],
            href: `/album?path=${encodeURIComponent(path)}&referrer=timeline`,
            count: this.getCount(`${decade}s/${year}`)
          }))
        }
        if (data && data[year]) {
          for (let leaf of data[year]['<leaves>']) {
            if (TIMELINE_SEASONS.indexOf(leaf) === -1) {
              const path = `/COLLECTIONS/${library}/${decade}s/${year}/${leaf}`
              periods.push(this.renderPeriod({
                title: `${library} » ${year} » ${leaf}`,
                path: [path],
                href: `/album?path=${encodeURIComponent(path)}&referrer=timeline`,
                count: this.getCount(`${decade}s/${year}/${leaf}`)
              }))
            }
          }
        }
      } else {
        const data = hierarchy.data[library]
        if (data && data['<leaves>'].indexOf(`${decade}s`) !== -1 && !data[`${decade}s`]) {
          const path = `/COLLECTIONS/${library}/${decade}s`
          periods.push(this.renderPeriod({
            title: `Other pictures in ${decade}s (${library})`,
            path: [path],
            href: `/album?path=${encodeURIComponent(path)}&referrer=timeline`,
            count: this.getCount(`${decade}s`)
          }))
        }
        if (data && data[`${decade}s`]) {
          for (let leaf of data[`${decade}s`]['<leaves>']) {
            if (/^\d\d\d\d$/.test(leaf) === false && leaf !== 'Unknown Year') {
              const path = `/COLLECTIONS/${library}/${decade}s/${leaf}`
              periods.push(this.renderPeriod({
                title: `${library} » ${leaf}`,
                path: [path],
                href: `/album?path=${encodeURIComponent(path)}&referrer=timeline`,
                count: this.getCount(`${decade}s/${leaf}`)
              }))
            }
          }
        }
      }
      return periods
    }, [])
  }
  getSeason (decade, year, season) {
    const { hierarchy } = this.props

    let events = hierarchy.data['<leaves>'].reduce((periods, library) => {
      try {
        const events = hierarchy.data[library][`${decade}s`][year][season]['<leaves>'].sort().filter(filterEvents)
        periods = periods.concat(events.filter(filterEvents).map(event => {
          const subevents = hierarchy.data[library][`${decade}s`][year][season][event]
          const path = `/COLLECTIONS/${library}/${decade}s/${year}/${season}/${event}`
          return {
            title: event,
            path: [path],
            href: `/album?path=${encodeURIComponent(path)}&referrer=timeline`,
            count: this.getCount(`${decade}s/${year}/${season}/${event}`),
            subevents: subevents && subevents['<leaves>'].map(subevent => ({
              title: subevent,
              path: [path + '/' + subevent],
              href: `/album?path=${encodeURIComponent(path + '/' + subevent)}&referrer=timeline`,
              count: this.getCount(`${decade}s/${year}/${season}/${event}/${subevent}`),
              subevent: true
            }))
          }
        }))
        return periods
      } catch (e) {
        return periods
      }
    }, []).map(this.renderPeriod)

    return events
  }
  getPeriods () {
    const { params } = this.props
    const { decade, year, season } = params

    const periods = []

    if (season) {
      const nodes = []
      const { showNext, showPrevious } = this.state
      for (let i = showPrevious; i <= showNext; i++) {
        const seasonIndex = TIMELINE_SEASONS.indexOf(season) + i
        const newSeason = TIMELINE_SEASONS[mod(seasonIndex, TIMELINE_SEASONS.length)]
        let newYear = parseInt(year) + Math.floor(seasonIndex / TIMELINE_SEASONS.length)
        let newDecade = parseInt(newYear / 10, 10) * 10
        if (year === 'Unknown Year') {
          newYear = year
          newDecade = decade
        }
        nodes.push(
          <StickyContainer key={i}>
            {this.renderBreadcrumbs(newDecade, newYear, newSeason)}
            {this.getSeason(newDecade, newYear, newSeason)}
          </StickyContainer>
        )
      }
      return nodes
    }

    if (year) {
      let nodes = []
      const { showNext, showPrevious } = this.state
      for (let i = showPrevious; i <= showNext; i++) {
        let newYear = parseInt(year) + i
        let newDecade = parseInt(newYear / 10, 10) * 10
        if (year === 'Unknown Year') {
          newYear = year
          newDecade = decade
        }
        const others = this.getOthers(newDecade, newYear)
        nodes.push(
          <StickyContainer key={i}>
            {this.renderBreadcrumbs(newDecade, newYear)}
            {TIMELINE_SEASONS.map(i => (
              this.renderPeriod({
                title: i,
                terms: [ `${newDecade}s`, newYear, i ],
                href: `/timeline/${newDecade}/${newYear}/${i}`,
                count: this.getCount(`${newDecade}s/${newYear}/${i}`),
                hideIfZero: i === UNKNOWN_SEASON || i === YEAR_SELECTS
              })
            ))}
            {others.length !== 0 &&
              <li className={classNames.gap} />
            }
            {others}
          </StickyContainer>
        )
      }
      return nodes
    }

    if (decade) {
      periods.push(this.renderBreadcrumbs(decade))
      for (let i = parseInt(decade); i < (parseInt(decade) + 10); i++) {
        if (i > CURRENT_YEAR) continue
        periods.push(this.renderPeriod({
          title: i,
          terms: [ `${decade}s`, i ],
          href: `/timeline/${decade}/${i}`,
          count: this.getCount(`${decade}s/${i}`)
        }, periods))
      }
      periods.push(this.renderPeriod({
        title: 'Unknown Year',
        terms: [ `${decade}s`, 'Unknown Year' ],
        href: `/timeline/${decade}/Unknown Year`,
        count: this.getCount(`${decade}s/Unknown Year`),
        hideIfZero: true
      }))
      return periods
    }

    for (let i = STARTING_DECADE; i <= CURRENT_DECADE; i += 10) {
      const title = `${i}s`
      const count = this.getCount(title)
      if (periods.length === 0 && !count) {
        continue
      }
      periods.push(this.renderPeriod({
        title,
        terms: [ title ],
        href: `/timeline/${i}`,
        count
      }))
    }
    return periods
  }
  getCount (path) {
    const { hierarchy, pathCounts } = this.props
    if (!pathCounts.data) {
      return 0
    }
    let count = 0
    for (let library of hierarchy.data['<leaves>']) {
      const key = `/COLLECTIONS/${library}/${path}`
      count += (pathCounts.data[key] || 0)
    }
    return count
  }
  showPrevious = () => {
    const prevHeight = document.body.scrollHeight
    this.setState({
      showPrevious: this.state.showPrevious - 1
    }, () => {
      window.scrollBy(0, document.body.scrollHeight - prevHeight)
    })
  }
  showNext = () => {
    this.setState({
      showNext: this.state.showNext + 1
    })
  }
  render () {
    const { params, hierarchy, pathCounts } = this.props
    const { decade, year, season } = params
    const others = !season && !year ? this.getOthers(decade) : []

    if (!hierarchy.data || !pathCounts.data) {
      return (
        <div className={classNames.page}>
          <LoadingSpinner />
        </div>
      )
    }

    return (
      <StickyContainer className={classNames.page}>
        <ul className={classNames.periods}>
          {year && year !== 'Unknown Year' &&
            <li>
              <button onClick={this.showPrevious} className={classNames.button}>
                Load earlier
              </button>
            </li>
          }
          {this.getPeriods()}
          {others.length !== 0 &&
            <li className={classNames.gap} />
          }
          {others}
          {year && year !== 'Unknown Year' &&
            <li>
              <button onClick={this.showNext} className={classNames.button}>
                Load next
              </button>
            </li>
          }
        </ul>
      </StickyContainer>
    )
  }
}

// http://stackoverflow.com/a/1082938
function mod (x, m) {
  return (x % m + m) % m
}

const mapStateToProps = ({ search, auth, hierarchy }) => ({
  search,
  hierarchy,
  pathCounts: hierarchy.paths,
  getImageSource: createImageGetter(auth)
})

const mapDispatchToProps = {
  searchImages,
  fetchHierarchy,
  fetchAlbumPathCounts
}

export default connect(mapStateToProps, mapDispatchToProps)(Timeline)

function filterEvents (event) {
  return /^_/.test(event) === false
}



// WEBPACK FOOTER //
// ./src/pages/Timeline.js